import { useMemo } from 'react';
import { DEFAULT_ROLES, type DrillRolePlayer, type PlayerRoleDef } from './playerRoles';
import { EQUIPMENT_GROUPS, type EquipmentVariant } from './equipmentTypes';
import { DRAWING_TOOLS, type DrawingMode } from './drawingTypes';
import { EquipmentMiniIcon } from './EquipmentMiniIcon';
import { HudChip } from '../tacticsHud/HudChip';
import '../tacticsHud/tacticsHud.css';
import { glassSurface, glassInput } from './liquidGlass';

interface DrillToolRailProps {
  // Players
  targetCount: number;
  onTargetCountChange: (n: number) => void;
  activeRoleId: string | null;
  onActiveRoleChange: (id: string | null) => void;
  placed: DrillRolePlayer[];
  customRoles?: PlayerRoleDef[];
  // Drawing
  activeDrawMode: DrawingMode | null;
  onActiveDrawModeChange: (mode: DrawingMode | null) => void;
  arrowCount: number;
  // Equipment
  activeEquipmentTool: EquipmentVariant | null;
  onActiveEquipmentToolChange: (tool: EquipmentVariant | null) => void;
}

/**
 * Drill canvas left rail — reuses the tactics HUD's chip + label
 * primitives so the look + feel matches the rest of the app. Sections:
 *
 *   1. Players  — role chips + target count
 *   2. Draw     — arrow mode chips with tooltips
 *   3. Markers / Obstacles / Goals — equipment groups
 *
 * Single mutex across sections — arming one disarms the others. The
 * "Select" row disarms everything.
 */
export function DrillToolRail({
  targetCount, onTargetCountChange,
  activeRoleId, onActiveRoleChange, placed, customRoles,
  activeDrawMode, onActiveDrawModeChange, arrowCount,
  activeEquipmentTool, onActiveEquipmentToolChange,
}: DrillToolRailProps): React.JSX.Element {
  const allRoles = useMemo(() => [...DEFAULT_ROLES, ...(customRoles ?? [])], [customRoles]);
  const totalPlaced = placed.length;
  const countByRole = useMemo(() => {
    const m: Record<string, number> = {};
    for (const p of placed) m[p.roleId] = (m[p.roleId] ?? 0) + 1;
    return m;
  }, [placed]);

  const nothingArmed = activeRoleId === null && activeDrawMode === null && activeEquipmentTool === null;

  const handleSelect = () => {
    onActiveRoleChange(null);
    onActiveDrawModeChange(null);
    onActiveEquipmentToolChange(null);
  };

  return (
    <div style={trayStyle} aria-label="Drill tool rail">
      {/* GLOBAL SELECT / MOVE */}
      <HudChip
        active={nothingArmed}
        onClick={handleSelect}
        title="Disarm any tool — click + drag tokens, arrows, etc."
      >
        ↖ Select / move
      </HudChip>

      {/* SECTION — PLAYERS */}
      <Section
        label="Players"
        right={<span className="thud__label">{totalPlaced}/{targetCount > 0 ? targetCount : '–'}</span>}
      >
        <div style={countRow}>
          <label className="thud__label">Target count</label>
          <input
            type="number"
            value={targetCount || ''}
            min={0}
            max={30}
            onChange={(e) => onTargetCountChange(Number(e.target.value) || 0)}
            style={{ ...glassInput, padding: '4px 8px', fontSize: 12, width: 60 }}
          />
        </div>
        {allRoles.map((r) => {
          const isActive = activeRoleId === r.id;
          const placedHere = countByRole[r.id] ?? 0;
          return (
            <HudChip
              key={r.id}
              active={isActive}
              activeColor={r.color}
              title={`${r.label} — click pitch to place`}
              onClick={() => {
                if (isActive) onActiveRoleChange(null);
                else {
                  onActiveRoleChange(r.id);
                  onActiveDrawModeChange(null);
                  onActiveEquipmentToolChange(null);
                }
              }}
            >
              <span style={chipInner}>
                <span style={{ ...colorChip, background: r.color }} />
                <span style={chipLabel}>{r.label}</span>
                {placedHere > 0 && <span style={countBadge}>{placedHere}</span>}
              </span>
            </HudChip>
          );
        })}
      </Section>

      {/* SECTION — DRAW */}
      <Section
        label="Draw"
        right={arrowCount > 0 ? <span className="thud__label">{arrowCount}</span> : undefined}
      >
        {DRAWING_TOOLS.map((t) => {
          const isActive = activeDrawMode === t.id;
          return (
            <HudChip
              key={t.id}
              active={isActive}
              activeColor={t.color}
              title={t.description}
              onClick={() => {
                if (isActive) onActiveDrawModeChange(null);
                else {
                  onActiveDrawModeChange(t.id);
                  onActiveRoleChange(null);
                  onActiveEquipmentToolChange(null);
                }
              }}
            >
              <span style={chipInner}>
                <span style={{ ...arrowGlyph, color: t.color }}>{t.glyph}</span>
                <span style={chipLabel}>{t.label}</span>
              </span>
            </HudChip>
          );
        })}
      </Section>

      {/* SECTIONS — EQUIPMENT */}
      {EQUIPMENT_GROUPS.map((group) => (
        <Section key={group.label} label={group.label}>
          <div style={equipmentGrid}>
            {group.tools.map((tool) => {
              const isActive = activeEquipmentTool === tool.id;
              return (
                <HudChip
                  key={tool.id}
                  active={isActive}
                  className="drill-equipment-chip"
                  title={tool.label}
                  onClick={() => {
                    if (isActive) onActiveEquipmentToolChange(null);
                    else {
                      onActiveEquipmentToolChange(tool.id);
                      onActiveRoleChange(null);
                      onActiveDrawModeChange(null);
                    }
                  }}
                >
                  <span style={equipmentInner}>
                    <EquipmentMiniIcon variant={tool.id} size={22} tint={tool.tint} />
                    <span style={equipmentLabel}>{tool.label}</span>
                  </span>
                </HudChip>
              );
            })}
          </div>
        </Section>
      ))}
    </div>
  );
}

function Section({ label, right, children }: { label: string; right?: React.ReactNode; children: React.ReactNode }): React.JSX.Element {
  return (
    <section style={sectionStyle}>
      <header style={sectionHeaderStyle}>
        <span className="thud__label">{label}</span>
        {right}
      </header>
      <div style={{ display: 'flex', flexDirection: 'column', gap: 4 }}>{children}</div>
    </section>
  );
}

// ---- styles ---------------------------------------------------------------
const trayStyle: React.CSSProperties = {
  ...glassSurface,
  display: 'flex', flexDirection: 'column', gap: 12,
  padding: 12,
  minWidth: 220, maxWidth: 240,
  maxHeight: '100%', overflowY: 'auto',
};
const sectionStyle: React.CSSProperties = {
  display: 'flex', flexDirection: 'column', gap: 6,
  paddingBottom: 8,
  borderBottom: '1px solid rgba(255,255,255,0.06)',
};
const sectionHeaderStyle: React.CSSProperties = {
  display: 'flex', alignItems: 'center', justifyContent: 'space-between',
  gap: 8,
};
const countRow: React.CSSProperties = {
  display: 'flex', alignItems: 'center', gap: 8,
  padding: '4px 0', borderBottom: '1px dashed rgba(255,255,255,0.06)',
  marginBottom: 4,
};
// Inner flex container — HudChip's button has no gap by default, so
// when a chip has multiple children (icon + label + count) they
// collapse together. Wrapping in chipInner gives consistent spacing.
const chipInner: React.CSSProperties = {
  display: 'inline-flex', alignItems: 'center', gap: 8,
  width: '100%',
};
const chipLabel: React.CSSProperties = {
  flex: 1, textAlign: 'left',
};
// Player role colour indicator — larger circle so the chip reads as
// "a player of this colour" at a glance. Bumped back to 14px and a
// stronger border for visual weight.
const colorChip: React.CSSProperties = {
  width: 14, height: 14, borderRadius: '50%',
  border: '1.5px solid rgba(0,0,0,0.4)',
  flexShrink: 0,
  boxShadow: '0 0 0 1px rgba(255,255,255,0.18)',
};
const arrowGlyph: React.CSSProperties = {
  width: 16, fontSize: 15, fontWeight: 700, lineHeight: 1, textAlign: 'center',
  flexShrink: 0,
};
const countBadge: React.CSSProperties = {
  background: 'rgba(255,255,255,0.18)',
  padding: '1px 6px', borderRadius: 999,
  fontSize: 10, fontWeight: 700, minWidth: 16, textAlign: 'center',
};
const equipmentGrid: React.CSSProperties = {
  display: 'grid', gridTemplateColumns: 'repeat(2, 1fr)', gap: 6,
};
const equipmentInner: React.CSSProperties = {
  display: 'flex', flexDirection: 'column', alignItems: 'center', justifyContent: 'center',
  gap: 2,
};
const equipmentLabel: React.CSSProperties = {
  fontSize: 9.5, opacity: 0.85, textAlign: 'center',
};
